import { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  MapPin,
  Calendar,
  Ticket,
} from 'lucide-react';
import { getMyTickets } from '../../stores/actions/ticketActions';
import { getGuestTickets, appendGuestTickets } from '../../utils/utils';
import { baseUrl } from '../../constant/constant';
import { scheduleDocumentScrollLockRelease } from '../../utils/scrollLock';
import {
  setPurchasedTickets,
  showPurchasedTicket,
} from '../../stores/slices/ticketSlice';
import { printTickets, InlineTicketView, formatDateTime } from './ticketCards';

const trimBarcode = (value) =>
  typeof value === 'string' ? value.trim() : value || '';

// Adapt the API's order-anchor shape (event.title/image/address,
// confirmation_id, type, and per-attendee `stubs[]`) to the flat fields this
// page's JSX renders. Dates and barcodes live on the stubs, so we surface the
// first stub's values at the top level for the card/summary while keeping the
// full `stubs` array for per-attendee QR codes in the viewer.
const normalizeTicket = (t) => {
  if (!t) return t;
  const stubs = Array.isArray(t.stubs) ? t.stubs : [];
  const first = stubs[0] || {};
  const ev = t.event || {};
  const ticketBarcode = trimBarcode(t.barcode);
  const firstBarcode = trimBarcode(first.barcode);
  const anyStubBarcode = trimBarcode(
    stubs.find((s) => trimBarcode(s?.barcode))?.barcode
  );
  const sharedBarcode = anyStubBarcode || firstBarcode || ticketBarcode;
  const stubSource = stubs.length
    ? stubs
    : sharedBarcode
      ? [{
          confirmation_id: t.confirmation_id || t.order_id || '',
          ticket_type: t.ticket_type || t.type || 'General Admission',
          barcode: sharedBarcode,
          activation_date: t.activation_date,
          expiration_date: t.expiration_date,
        }]
      : [];
  const normalizedStubs = stubSource.map((s) => ({
    ...s,
    barcode: trimBarcode(s.barcode) || sharedBarcode,
  }));

  return {
    ...t,
    eventname: t.eventname || ev.title || '',
    order_id: t.order_id || t.confirmation_id || '',
    ticket_type: t.ticket_type || t.type || first.ticket_type || 'General Admission',
    activation_date: t.activation_date || first.activation_date || ev.date || null,
    expiration_date: t.expiration_date || first.expiration_date || ev.end_date || null,
    barcode: sharedBarcode,
    qty: t.qty ?? normalizedStubs.length ?? 1,
    stubs: normalizedStubs,
    event: {
      ...ev,
      flyer1: ev.flyer1 || ev.image || null,
      horizontal_flyer: ev.horizontal_flyer || ev.image || null,
      venueaddress: ev.venueaddress || ev.address || '',
    },
  };
};

// Guests have no bearer token, so the auth-only /my-tickets endpoint can't be
// called for them (it would 401 and the interceptor would wipe their cookie +
// reload). Their just-purchased tickets instead live in Redux as a flat array
// of per-attendee stubs (state.tickets.purchased_tickets, set on payment
// success). Group those stubs by event into the same card shape the page
// renders for authed users.
const groupPurchasedStubs = (stubs) => {
  if (!Array.isArray(stubs) || stubs.length === 0) return [];
  const groups = {};
  stubs.forEach((s) => {
    const key = s.event_id ?? s.eventname ?? 'order';
    if (!groups[key]) groups[key] = [];
    groups[key].push(s);
  });
  return Object.values(groups).map((group) => {
    const first = group[0] || {};
    return normalizeTicket({
      id: first.id,
      confirmation_id: first.confirmation_id,
      type: first.ticket_type,
      qty: group.length,
      eventname: first.eventname,
      activation_date: first.activation_date,
      expiration_date: first.expiration_date,
      barcode: first.barcode,
      event: first.event || { name: first.eventname },
      stubs: group,
    });
  });
};

// The API paginates over ORDER anchors, so a user with two separate purchases
// for the same event gets two list entries, each with a duplicated `event`
// block. Collapse those into a single card per event: keep the first anchor as
// the base (its id anchors selection), concatenate every anchor's stubs into
// one list, and sum the qty so the card/viewer show all tickets together.
const groupTicketsByEvent = (tickets) => {
  if (!Array.isArray(tickets) || tickets.length === 0) return [];
  const groups = new Map();
  const order = [];
  tickets.forEach((t) => {
    const key = t?.event_id ?? t?.event?.id ?? t?.eventname ?? t?.id;
    if (!groups.has(key)) {
      groups.set(key, { ...t, stubs: [...(t.stubs || [])] });
      order.push(key);
    } else {
      const base = groups.get(key);
      base.stubs = [...base.stubs, ...(t.stubs || [])];
    }
  });
  return order.map((key) => {
    const base = groups.get(key);
    return { ...base, qty: base.stubs.length || base.qty || 1 };
  });
};

const getViewerTickets = (order) =>
  (order?.stubs || []).map((stub) => ({
    ...stub,
    event: order.event,
    eventname:
      order.eventname || order.event?.title || order.event?.name || '',
  }));

// Main MyTicket Component
export default function MyTicket() {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('upcoming');
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [currentStubIndex, setCurrentStubIndex] = useState(0);

  const ticketViewRef = useRef(null);

  // Local loading flags tied to the actual fetch promises. The global
  // `common.loading` boolean is unreliable here because it flips false after
  // the FIRST of the two concurrent (upcoming + expired) fetches resolves,
  // which would hide the skeletons early. `loading` covers the initial/tab
  // load (drives skeletons); `loadingMore` covers Load More.
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Live data from Redux (populated by the getMyTickets thunk).
  const upcomingRaw = useSelector((state) => state.tickets.upcoming);
  const expiredRaw = useSelector((state) => state.tickets.expired);
  const totalUpcomingApi = useSelector((state) => state.tickets.total_upcoming);
  const totalExpiredApi = useSelector((state) => state.tickets.total_expired);
  const purchasedTickets = useSelector((state) => state.tickets.purchased_tickets);
  const pagination = useSelector((state) => state.tickets.pagination);
  const user = useSelector((state) => state.auth.user);
  // The real auth signal is the bearer token (`_aps_t`). `isLoggedIn`/`user`
  // are also set for guests, so gate every fetch on `token` to avoid a
  // token-less /my-tickets request that 401s. No token ⇒ treat as guest.
  const token = useSelector((state) => state.auth.token);
  const isGuest = !token;

  // Separate Upcoming / Expired lists, normalized to the flat shape the JSX
  // renders. Guests can't hit the auth API, so their Upcoming tab shows their
  // purchased tickets — merged from this session's Redux `purchased_tickets`
  // and the localStorage cache (which survives the success-modal close and a
  // reload) — deduped by id. Their Expired tab is empty.
  const guestUpcoming = useMemo(() => {
    if (!isGuest) return [];
    const byKey = new Map();
    [...(purchasedTickets || []), ...getGuestTickets()].forEach((s) => {
      const key = s?.id ?? s?.confirmation_id;
      if (key != null && !byKey.has(key)) byKey.set(key, s);
    });
    return groupPurchasedStubs(Array.from(byKey.values()));
  }, [isGuest, purchasedTickets]);
  const upcoming = useMemo(
    () =>
      isGuest
        ? guestUpcoming
        : groupTicketsByEvent((upcomingRaw || []).map(normalizeTicket)),
    [isGuest, guestUpcoming, upcomingRaw]
  );
  const expired = useMemo(
    () =>
      isGuest
        ? []
        : groupTicketsByEvent((expiredRaw || []).map(normalizeTicket)),
    [isGuest, expiredRaw]
  );
  // Count distinct events (grouped cards), not raw order anchors, so the tab
  // label matches the number of cards actually rendered after grouping.
  const total_upcoming = isGuest ? guestUpcoming.length : upcoming.length;
  const total_expired = isGuest ? 0 : expired.length;

  // ---- Guest barcode refresh -------------------------------------------
  // A guest's QR barcode is generated asynchronously (GenerateTicketArtifactsJob,
  // QUEUE_CONNECTION=database) a few seconds AFTER purchase, and the guest cache
  // (localStorage + Redux purchased_tickets) is seeded with an EMPTY barcode.
  // Unlike authed users — who re-fetch /my-tickets and get a freshly-signed URL
  // every load — guests are gated out of the auth API, so a blank barcode would
  // stay "Generating QR…" forever, and even a populated one is a ~1h signed S3
  // URL that 403s once cached past its TTL. Mirror the post-purchase modal: poll
  // the PUBLIC ticket-lookup/{confirmation_id} endpoint (which re-signs), then
  // patch the fresh barcodes back into BOTH the localStorage cache and Redux so
  // the cards update and the fix survives a reload.
  const hasBarcode = (s) =>
    typeof s?.barcode === 'string' && s.barcode.trim() !== '';

  // Every guest anchor confirmation id currently on screen. We refresh ALL of
  // them (not just the empty ones) so stale/expired 1h signed URLs get re-signed
  // on each visit too — matching what authed users get from /my-tickets. Keyed
  // off a stable joined string so the poll only (re)starts when the SET of guest
  // orders changes, never on our own barcode-patch dispatch.
  const guestOrderCids = useMemo(() => {
    if (!isGuest) return [];
    return Array.from(
      new Set(
        guestUpcoming
          .map((t) => t?.confirmation_id || t?.stubs?.[0]?.confirmation_id)
          .filter(Boolean)
      )
    );
  }, [isGuest, guestUpcoming]);
  const guestOrderKey = guestOrderCids.join('|');

  // Latest purchased_tickets + a snapshot of which stubs still lack a barcode,
  // read inside the poll via refs so neither is an effect dep (that would
  // restart the poll on our own dispatch).
  const purchasedRef = useRef(purchasedTickets);
  useEffect(() => {
    purchasedRef.current = purchasedTickets;
  }, [purchasedTickets]);
  const anyBarcodeMissingRef = useRef(false);
  anyBarcodeMissingRef.current = guestUpcoming.some((t) =>
    (t?.stubs || []).some((s) => !hasBarcode(s))
  );
  // The mobile viewer temporarily repurposes purchased_tickets to hold just the
  // opened order's stubs, so don't clobber it with the full guest set while it's
  // open — the localStorage cache still gets patched, so the cards refresh from
  // it once the viewer closes.
  const viewerOpen = useSelector((s) => s.tickets.show_purchased_ticket);
  const viewerOpenRef = useRef(viewerOpen);
  viewerOpenRef.current = viewerOpen;

  useEffect(() => {
    if (!isGuest || guestOrderCids.length === 0) return undefined;

    let cancelled = false;
    let timer = null;
    let attempts = 0;

    const poll = async () => {
      attempts += 1;
      // stubKey -> fresh signed barcode
      const freshByKey = new Map();

      await Promise.all(
        guestOrderCids.map(async (cid) => {
          try {
            const res = await fetch(
              `${baseUrl}/ticket-lookup/${encodeURIComponent(cid)}`
            );
            const json = await res.json();
            const stubs = json?.data?.stubs;
            if (!Array.isArray(stubs)) return;
            stubs.forEach((s) => {
              const key = s?.id ?? s?.confirmation_id;
              if (key != null && hasBarcode(s)) freshByKey.set(key, s.barcode);
            });
          } catch (_) {
            // transient — a later attempt will retry
          }
        })
      );

      if (cancelled) return;

      if (freshByKey.size) {
        // Patch barcodes onto the flat guest stub set (same source guestUpcoming
        // merges), preserving each stub's event_id / event / eventname so the
        // grouping and card rendering stay intact.
        const byKey = new Map();
        [...(purchasedRef.current || []), ...getGuestTickets()].forEach((s) => {
          const key = s?.id ?? s?.confirmation_id;
          if (key != null && !byKey.has(key)) byKey.set(key, s);
        });
        const patched = Array.from(byKey.values()).map((s) => {
          const key = s?.id ?? s?.confirmation_id;
          const bc = key != null ? freshByKey.get(key) : null;
          return bc ? { ...s, barcode: bc } : s;
        });
        // Persist so the barcodes survive a reload, and (unless the viewer is
        // open) push into Redux so guestUpcoming — which depends on
        // purchased_tickets — re-renders with the signed barcodes.
        appendGuestTickets(patched);
        if (!viewerOpenRef.current) {
          dispatch(setPurchasedTickets(patched));
        }
      }

      // Keep polling only while some stub is still awaiting its async QR image;
      // once all barcodes are present the first pass already re-signed them, so
      // we stop. (~1 min budget for the GenerateTicketArtifactsJob to finish.)
      if (!cancelled && anyBarcodeMissingRef.current && attempts < 15) {
        timer = setTimeout(poll, 4000);
      }
    };

    timer = setTimeout(poll, 0);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // guestOrderCids is captured intentionally; guestOrderKey is the stable
    // signal that the set of guest orders changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGuest, guestOrderKey, dispatch]);
  // ----------------------------------------------------------------------

  const filteredTickets = activeTab === 'upcoming' ? upcoming : expired;
  const selectedTicket = useMemo(() => {
    if (!filteredTickets.length) return null;
    return (
      filteredTickets.find((t) => t.id === selectedTicketId) ||
      filteredTickets[0]
    );
  }, [filteredTickets, selectedTicketId]);

  const openMobileViewer = (ticket) => {
    if (!ticket) return;
    dispatch(setPurchasedTickets(getViewerTickets(ticket)));
    dispatch(showPurchasedTicket(true));
  };

  useLayoutEffect(() => scheduleDocumentScrollLockRelease(), []);

  useEffect(() => {
    if (loading) return undefined;
    return scheduleDocumentScrollLockRelease();
  }, [loading]);

  // `fetchType` maps the UI tab ('past') to the API's 'expired' type.
  const fetchType = activeTab === 'upcoming' ? 'upcoming' : 'expired';

  // Fetch BOTH lists once the token is available (incl. on a fresh reload,
  // when the token is rehydrated from the `_aps_t` cookie). We only gate on
  // `token` — the backend authorizes via the bearer token (`Auth::id()`) and
  // ignores `user_id`. Fetching expired here too means its tab + count are
  // ready without a tab click.
  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([
      dispatch(getMyTickets({ type: 'upcoming', user_id: user?.id })),
      dispatch(getMyTickets({ type: 'expired', user_id: user?.id })),
    ]).finally(() => setLoading(false));
  }, [token]);

  // Refetch the active tab on switch so its pagination state reflects the
  // visible list (the slice keeps a single shared `pagination`). Skip the
  // initial mount run — the effect above already fetched both lists.
  const didMountTab = useRef(false);
  useEffect(() => {
    if (!didMountTab.current) {
      didMountTab.current = true;
      return;
    }
    if (!token) return;
    setLoading(true);
    Promise.resolve(dispatch(getMyTickets({ type: fetchType, user_id: user?.id })))
      .finally(() => setLoading(false));
  }, [activeTab]);

  // Keep the detail pane pointed at a valid ticket id for the active list.
  useEffect(() => {
    if (!filteredTickets.length) {
      if (selectedTicketId !== null) setSelectedTicketId(null);
      return;
    }
    if (
      !selectedTicketId ||
      !filteredTickets.some((t) => t.id === selectedTicketId)
    ) {
      setSelectedTicketId(filteredTickets[0].id);
      setCurrentStubIndex(0);
    }
  }, [activeTab, filteredTickets, selectedTicketId]);

  const handleLoadMore = () => {
    if (!token) return;
    if (pagination?.current_page < pagination?.last_page) {
      setLoadingMore(true);
      Promise.resolve(dispatch(getMyTickets({ type: fetchType, user_id: user?.id, page: pagination.current_page + 1 })))
        .finally(() => setLoadingMore(false));
    }
  };

  const handleTicketSelect = (ticket) => {
    setSelectedTicketId(ticket.id);
    setCurrentStubIndex(0);
    if (window.innerWidth >= 1024 && ticketViewRef.current) {
      ticketViewRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handlePreviousStub = () => {
    if (selectedTicket && currentStubIndex > 0) {
      setCurrentStubIndex(currentStubIndex - 1);
    } else if (selectedTicket && currentStubIndex === 0) {
      // Navigate to previous ticket if available
      const currentList = activeTab === 'upcoming' ? upcoming : expired;
      const currentIndex = currentList.findIndex(t => t.id === selectedTicket.id);
      if (currentIndex > 0) {
        setSelectedTicketId(currentList[currentIndex - 1].id);
        const newTicketQty = currentList[currentIndex - 1].qty || 1;
        setCurrentStubIndex(newTicketQty - 1);
      }
    }
  };

  const handleNextStub = () => {
    const qty = selectedTicket?.qty || 1;
    if (selectedTicket && currentStubIndex < qty - 1) {
      setCurrentStubIndex(currentStubIndex + 1);
    } else if (selectedTicket && currentStubIndex === qty - 1) {
      // Navigate to next ticket if available
      const currentList = activeTab === 'upcoming' ? upcoming : expired;
      const currentIndex = currentList.findIndex(t => t.id === selectedTicket.id);
      if (currentIndex < currentList.length - 1) {
        setSelectedTicketId(currentList[currentIndex + 1].id);
        setCurrentStubIndex(0);
      }
    }
  };

  const formatDateTime = (dateString, to) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    let options = { month: 'short', day: 'numeric', year: 'numeric' };
    if (to === 'time') {
      options = { hour: 'numeric', minute: '2-digit', hour12: true };
      return date.toLocaleTimeString('en-US', options);
    }
    return date.toLocaleDateString('en-US', options);
  };

  // Print the selected ticket (one printable stub per attendee) in a clean
  // pop-up window so the app chrome/nav/list isn't printed. Shared with the
  // Lookup page via printTickets so both produce the identical printout.
  const handlePrint = () => {
    if (!selectedTicket) return;
    const t = selectedTicket;
    const stubs = (t.stubs && t.stubs.length)
      ? t.stubs
      : [{ barcode: t.barcode, confirmation_id: t.order_id, ticket_type: t.ticket_type }];
    printTickets({
      eventName: t.eventname,
      dateStr: formatDateTime(t.activation_date, 'date'),
      timeStr: `${formatDateTime(t.activation_date, 'time')} - ${formatDateTime(t.expiration_date, 'time')}`,
      venueFull: t.event?.venueaddress || '',
      venueShort: (t.event?.venueaddress || '').split(',')[0] || 'Venue',
      isPast: activeTab === 'past',
      total: t.qty || stubs.length || 1,
      stubs: stubs.map((s, i) => ({
        confirmation_id: s.confirmation_id || `${t.order_id || `APS-${t.id}`}-${i + 1}`,
        ticket_type: s.ticket_type || t.ticket_type || 'General Admission',
        // Matches InlineTicketView's Ticket Holder field, which reads the
        // per-stub name and falls back to an em dash.
        holder_name: [s.firstname, s.lastname].filter(Boolean).join(' ').trim() || 'Guest',
        barcode: s.barcode || t.barcode,
      })),
    });
  };

  const upcomingCount = total_upcoming;
  const pastCount = total_expired;

  return (
    <div className="min-h-[calc(100vh-80px)] bg-gray-50 py-12 flex flex-col">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 pb-6 border-b border-gray-200">
          <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-4 sm:mb-0">My Tickets</h2>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-start">
          {/* 40% + 60% + a 3rem gap is wider than the row, so both columns were
              being shrunk off their stated widths. Fix the rail's basis and let
              the ticket pane take whatever is left. */}
          <div className="w-full lg:basis-[40%] lg:shrink-0 flex flex-col space-y-6 lg:sticky lg:top-28 lg:self-start lg:max-h-[calc(100vh-8rem)]">
            <div className="rounded-lg overflow-hidden border-b border-gray-200 shadow-sm">
              <div className="flex items-stretch w-full">
                <div
                  onClick={() => { setActiveTab('upcoming'); setSelectedTicketId(upcoming[0]?.id ?? null); setCurrentStubIndex(0); }}
                  className={`relative flex-1 text-center cursor-pointer px-4 py-3.5 text-[11px] md:text-[13px] font-black uppercase tracking-wide transition-colors ${
                    activeTab === 'upcoming'
                      ? 'text-black'
                      : 'text-[#9ca3af] hover:text-gray-700'
                  }`}
                >
                  Upcoming Events ({upcomingCount})

                  {activeTab === 'upcoming' && (
                    <div className="absolute bottom-0 left-0 w-full h-[3px] bg-[#ffe600]" />
                  )}
                </div>

                <div
                  onClick={() => { setActiveTab('past'); setSelectedTicketId(expired[0]?.id ?? null); setCurrentStubIndex(0); }}
                  className={`relative flex-1 text-center cursor-pointer px-4 py-3.5 text-[11px] md:text-[13px] font-black uppercase tracking-wide transition-colors ${
                    activeTab === 'past'
                      ? 'text-black'
                      : 'text-[#9ca3af] hover:text-gray-700'
                  }`}
                >
                  Expired Events ({pastCount})

                  {activeTab === 'past' && (
                    <div className="absolute bottom-0 left-0 w-full h-[3px] bg-[#ffe600]" />
                  )}
                </div>
              </div>
            </div>


            <div className="flex flex-col space-y-4 pr-2 pt-1 lg:flex-1 lg:min-h-0 lg:overflow-y-auto">
              {!loading && filteredTickets.map((ticket) => {
                const isSelected = selectedTicketId === ticket.id;
                const qty = ticket.qty || 1;
                return (
                  <div
                    key={ticket.id}
                    data-ticket-card="1"
                    onClick={() => handleTicketSelect(ticket)}
                    className={`bg-white rounded-2xl cursor-pointer transition-all border-2 flex flex-col ${
                      isSelected ? 'border-[#ffea00] shadow-lg ring-4 ring-[#ffea00]/10 lg:scale-[1.02]' : 'border-gray-100 hover:border-gray-300 shadow-sm'
                    }`}
                    style={{border:"2px solid #ffea00"}}
                  >
                    <div className="relative aspect-[2/1] w-full overflow-hidden">
                      <img 
                        src={ticket?.event?.flyer1 || ticket?.event?.horizontal_flyer || "/assets/images/event-cover-paceholder.png"} 
                        alt={ticket.eventname} 
                        className="w-full h-full object-cover rounded-tl-2xl rounded-tr-2xl"
                        onError={({ currentTarget }) => {
                          currentTarget.onerror = null;
                          currentTarget.src = "/assets/images/event-cover-paceholder.png";
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                      <div className="absolute bottom-3 left-4 right-4">
                        <h4 className="text-white font-black text-lg truncate drop-shadow-md">{ticket.eventname}</h4>
                      </div>
                      <div className="hidden lg:flex absolute top-3 right-3 bg-[#ffea00] text-black text-xs font-black px-2.5 py-1.5 rounded-lg shadow-md items-center">
                        <Ticket size={14} className="mr-1.5" /> {qty} Ticket{qty !== 1 ? 's' : ''}
                      </div>
                    </div>
                    <div className="p-4 space-y-2 rounded-2xl bg-white flex flex-col">
                      <div className="flex items-center text-sm font-bold text-gray-500">
                        <Calendar size={16} className="mr-2 text-[#ffea00] flex-shrink-0" />
                        <span className="truncate">{formatDateTime(ticket.activation_date, 'date')}</span>
                      </div>
                      <div className="flex items-center text-sm font-bold text-gray-500">
                        <MapPin size={16} className="mr-2 text-gray-400 flex-shrink-0" />
                        <span className="truncate">{ticket?.event?.venueaddress?.split(',')[0] || 'Venue'}</span>
                      </div>
                      <button 
                        onClick={(e) => { e.stopPropagation(); openMobileViewer(ticket); }}
                        className="lg:hidden mt-3 w-full bg-black hover:bg-gray-800 text-[#ffea00] font-black py-3 border-none rounded-xl shadow-md transition-all flex items-center justify-center text-sm"
                      >
                        <Ticket size={16} className="mr-2" /> View Tickets ({qty})
                      </button>
                    </div>
                  </div>
                );
              })}
              
              {!isGuest && loading && (
                <>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div
                      key={`skeleton-${i}`}
                      className="bg-white rounded-2xl overflow-hidden animate-pulse"
                      style={{ border: '2px solid #ffea00' }}
                    >
                      <div className="aspect-[2/1] w-full bg-gray-200" />
                      <div className="p-4 space-y-3">
                        <div className="h-4 bg-gray-200 rounded w-1/2" />
                        <div className="h-4 bg-gray-200 rounded w-2/3" />
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Guest with no in-session purchase: tickets aren't tied to an
                  account, so point them at the email/confirmation lookup. */}
              {isGuest && filteredTickets.length === 0 && (
                <div className="text-center py-12 px-6 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                  <Ticket size={40} className="mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-700 font-bold text-lg mb-1">Purchased as a guest?</p>
                  <p className="text-gray-500 font-medium text-sm mb-5">Your tickets were emailed to you. Look them up with your confirmation ID.</p>
                  <a
                    href="/lookup"
                    className="inline-flex items-center justify-center bg-black hover:bg-gray-800 text-[#ffea00] font-black py-3 px-6 rounded-xl shadow-md transition-all text-sm no-underline"
                  >
                    Look up my tickets
                  </a>
                </div>
              )}

              {!isGuest && !loading && filteredTickets.length === 0 && (
                <div className="text-center py-12 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                  <Ticket size={40} className="mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500 font-bold text-lg">No {activeTab === 'upcoming' ? 'upcoming' : 'expired'} tickets found!</p>
                </div>
              )}

              {!isGuest && filteredTickets.length > 0 && pagination?.current_page < pagination?.last_page && (
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="mt-2 w-full bg-black hover:bg-gray-800 disabled:opacity-60 text-[#ffea00] font-black py-3 border-none rounded-xl shadow-md transition-all flex items-center justify-center text-sm"
                >
                  {loadingMore ? 'Loading…' : 'Load More'}
                </button>
              )}
            </div>
          </div>

          <div ref={ticketViewRef} className="hidden lg:block lg:flex-1 lg:min-w-0 sticky top-28 animate-pop-in">
            {!isGuest && loading ? (
              <div className="flex flex-col items-end">
                <div className="mb-4 h-8 w-28 bg-gray-200 rounded-lg animate-pulse" />
                <div className="w-full bg-white rounded-[2rem] shadow-2xl border border-gray-200 overflow-hidden animate-pulse">
                  <div className="bg-gray-200 h-28" />
                  <div className="bg-gray-300 h-12" />
                  <div className="p-8 md:p-12">
                    <div className="h-8 bg-gray-200 rounded w-2/3 mx-auto mb-10" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10 border-b border-gray-100 pb-10">
                      <div className="space-y-2">
                        <div className="h-3 bg-gray-200 rounded w-1/3" />
                        <div className="h-5 bg-gray-200 rounded w-2/3" />
                        <div className="h-4 bg-gray-200 rounded w-1/2" />
                      </div>
                      <div className="space-y-2">
                        <div className="h-3 bg-gray-200 rounded w-1/3" />
                        <div className="h-5 bg-gray-200 rounded w-2/3" />
                        <div className="h-4 bg-gray-200 rounded w-1/2" />
                      </div>
                    </div>
                    <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12">
                      <div className="w-56 h-56 md:w-64 md:h-64 bg-gray-200 rounded-2xl flex-shrink-0" />
                      <div className="space-y-5 w-full">
                        <div className="space-y-2">
                          <div className="h-3 bg-gray-200 rounded w-1/3" />
                          <div className="h-6 bg-gray-200 rounded w-2/3" />
                        </div>
                        <div className="space-y-2">
                          <div className="h-3 bg-gray-200 rounded w-1/3" />
                          <div className="h-6 bg-gray-200 rounded w-1/2" />
                        </div>
                        <div className="space-y-2">
                          <div className="h-3 bg-gray-200 rounded w-1/3" />
                          <div className="h-6 bg-gray-200 rounded w-1/3" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : selectedTicket ? (
              <InlineTicketView
                ticket={selectedTicket}
                stubIndex={currentStubIndex}
                setStubIndex={setCurrentStubIndex}
                isExpired={activeTab === 'past'}
                statusLabel={activeTab === 'upcoming' ? 'Upcoming' : 'Expired'}
                onPrint={handlePrint}
                onPreviousStub={handlePreviousStub}
                onNextStub={handleNextStub}
                canGoPrevious={
                  currentStubIndex > 0 ||
                  filteredTickets.findIndex((t) => t.id === selectedTicket.id) > 0
                }
                canGoNext={
                  currentStubIndex < (selectedTicket.qty || 1) - 1 ||
                  filteredTickets.findIndex((t) => t.id === selectedTicket.id) <
                    filteredTickets.length - 1
                }
              />
            ) : (
              <div className="w-full h-96 bg-gray-100 rounded-[2rem] border-2 border-dashed border-gray-300 flex items-center justify-center">
                <p className="text-gray-500 font-bold text-lg">Select a ticket to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}